/**
 * バイト列⇄文字列 の変換ヘルパ。
 * 全関数 pure / 例外なし設計 (不正入力は RangeError or empty)。
 *
 * UTF-8: TextEncoder / TextDecoder を使用 (Node 20+ / 全 evergreen ブラウザでネイティブ)
 * hex:   小文字固定で出力。入力は大文字小文字どちらも受け付ける
 * base64 / base64url: btoa / atob ベース (binary string 経由)
 */

const HEX_CHARS = '0123456789abcdef'
const enc = new TextEncoder()
const dec = new TextDecoder('utf-8', { fatal: true })

// ── UTF-8 ──────────────────────────────────────────────────────
export const utf8ToBytes = (s: string): Uint8Array => enc.encode(s)
export const bytesToUtf8 = (b: Uint8Array): string => dec.decode(b)

// ── hex ────────────────────────────────────────────────────────
export const bytesToHex = (b: Uint8Array): string => {
  let out = ''
  for (let i = 0; i < b.length; i++) {
    const v = b[i] as number

    out += HEX_CHARS[v >> 4]! + HEX_CHARS[v & 0x0f]!
  }

  return out
}

export const hexToBytes = (hex: string): Uint8Array => {
  const clean = hex.startsWith('0x') ? hex.slice(2) : hex
  if (clean.length % 2 !== 0) {
    throw new RangeError(`hex string must have even length: "${hex}"`)
  }
  const out = new Uint8Array(clean.length / 2)
  for (let i = 0; i < out.length; i++) {
    const byte = parseInt(clean.slice(i * 2, i * 2 + 2), 16)
    if (Number.isNaN(byte)) {
      throw new RangeError(`invalid hex char at offset ${i * 2}`)
    }
    out[i] = byte
  }

  return out
}

// ── base64 (standard, +/=) ─────────────────────────────────────
export const bytesToBase64 = (b: Uint8Array): string => {
  // 大きい配列でも動くよう分割 btoa
  let bin = ''
  const CHUNK = 0x8000
  for (let i = 0; i < b.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, Array.from(b.subarray(i, i + CHUNK)))
  }

  return btoa(bin)
}

export const base64ToBytes = (b64: string): Uint8Array => {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)

  return out
}

// ── base64url (URL safe, no padding) ───────────────────────────
export const bytesToBase64Url = (b: Uint8Array): string =>
  bytesToBase64(b).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

export const base64UrlToBytes = (b64url: string): Uint8Array => {
  let s = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = s.length % 4
  if (pad) s += '='.repeat(4 - pad)

  return base64ToBytes(s)
}

// ── 連結 ──────────────────────────────────────────────────────
export const concatBytes = (...arrs: Uint8Array[]): Uint8Array => {
  let len = 0
  for (const a of arrs) len += a.length
  const out = new Uint8Array(len)
  let off = 0
  for (const a of arrs) {
    out.set(a, off)
    off += a.length
  }

  return out
}
